import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { ROOMS } from '../../rooms';
import { roomSpace } from '../../state/interiorFloor';
import { roomWalker } from '../../state/roomBody';
import { useHypeU, DOOR_MS, elevatorBridge } from '../../city/hypeu';

// THE ELEVATOR'S DOORS, AND "YOU ARE IN THE CAR" (Hype-U, city/hypeu).
//
// In the room group (MeshInterior), so rooms.js `elevator` (room space) × `k` is
// where things are. The car is the walk-in box the builder made
// (scripts/lib/hypeu elevatorCore); these are the two steel panels in its
// opening, sliding apart:
//   · open while you are at the doors or in the car — walk up and they part;
//   · shut for the ride (city/hypeu rideTo: 'closing', 'moving');
//   · on arrival ('opening') a beat closed, then parting onto the new floor.
// Standing inside the car's box sets `inCar` (ElevatorPanel shows the floors).
//
// FOUR PANELS, TELESCOPING (2026-11-01, Khari: "the elevator doors show on the
// outside of the walls"): two half-width panels a side, the outer travelling
// half as far as the inner, so open they stack into the space behind the jamb
// (0.5m of jamb, 0.35m of stack) — inside its thickness, never past the car's
// side wall. Two leaves of 0.7m each slid out through the walls.
const OPEN_NEAR = 0.85;   // room units in front of the doors that part them
const tmp = new THREE.Vector3();

export default function Elevator({ roomId, k = 1 }) {
  const el = ROOMS[roomId]?.elevator;
  const camera = useThree((s) => s.camera);
  const panels = useRef([]);   // [left inner, left outer, right inner, right outer]
  const open = useRef(0);
  const doorGroup = useRef(null);
  // Published for ElevatorDirector: this room, its fit, and the group these numbers live in.
  useEffect(() => {
    elevatorBridge.roomId = roomId; elevatorBridge.k = k; elevatorBridge.space = doorGroup.current?.parent || null;
    return () => { if (elevatorBridge.roomId === roomId) { elevatorBridge.roomId = null; elevatorBridge.space = null; } };
  }, [roomId, k]);
  const g = useMemo(() => {
    if (!el) return null;
    const d = el.door;
    return { pos: d.pos.map((v) => v * k), w: d.w * k, h: d.h * k, rotY: d.rotY || 0, min: el.car.min.map((v) => v * k), max: el.car.max.map((v) => v * k) };
  }, [el, k]);

  useFrame((_, dt) => {
    if (!g || panels.current.length < 4) return;
    const hu = useHypeU.getState();
    const w = roomWalker(camera);
    const p = roomSpace.object ? roomSpace.object.worldToLocal(tmp.copy(w)) : tmp.copy(w);
    const inCar = p.x >= g.min[0] && p.x <= g.max[0] && p.z >= g.min[2] && p.z <= g.max[2];
    if (!hu.ride && inCar !== hu.inCar) useHypeU.setState(inCar ? { inCar } : { inCar, arrivedIn: false });
    const near = Math.hypot(p.x - g.pos[0], p.z - g.pos[2]) < OPEN_NEAR * k;
    let want = near || inCar ? 1 : 0;
    const phase = hu.ride?.phase;
    if (phase === 'closing' || phase === 'moving') want = 0;
    if (phase === 'opening' && performance.now() - hu.ride.at < 350) want = 0;   // a beat, then they part
    const step = dt / (DOOR_MS / 1000);
    open.current += THREE.MathUtils.clamp(want - open.current, -step, step);
    const q = g.w / 4;                       // a panel: half of one side
    const e = open.current * open.current * (3 - 2 * open.current);
    panels.current.forEach((m, i) => {
      if (!m) return;
      const s = i < 2 ? -1 : 1; const outer = i % 2 === 1;
      // Closed: inner over [0, q], outer over [q, 2q]. Open: both over [2q, 3q] — behind the jamb.
      const closed = outer ? 1.5 * q : 0.5 * q;
      m.position.x = s * (closed + e * (outer ? q : 2 * q));
    });
  });

  if (!g) return null;
  const q = g.w / 4;
  return (
    <group ref={doorGroup} position={g.pos} rotation={[0, g.rotY, 0]}>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} ref={(m) => { panels.current[i] = m; }} position={[(i < 2 ? -1 : 1) * (i % 2 ? 1.5 : 0.5) * q, g.h / 2, (i % 2 ? -0.012 : 0.006) * k]} castShadow>
          <boxGeometry args={[q + 0.006 * k, g.h, 0.01 * k]} />
          <meshStandardMaterial color={i % 2 ? '#a9adb3' : '#b9bdc3'} metalness={0.85} roughness={0.28} />
        </mesh>
      ))}
    </group>
  );
}
